import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { AlertTriangle, TrendingUp, Users, Clock } from "lucide-react";
import { api } from "../api";
import type { ComplianceWeek } from "../types";

const COLORS = ["#5f1b2c", "#e2c4b6", "#6366f1", "#22c55e", "#f59e0b", "#06b6d4"];

/** Normalize any date string to YYYY-MM-DD */
function toDateStr(d: string | null | undefined): string {
  if (!d) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const date = new Date(d);
  if (isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
}

function fmtDate(d: string | null): string {
  const ds = toDateStr(d);
  if (!ds) return "\u2014";
  const [y, m, dd] = ds.split("-");
  return `${dd}/${m}/${y}`;
}

export function Analytics() {
  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
      <ComplianceChart />
      <RepeatMisses />
      <PaymentIssuesList />
      <div className="grid md:grid-cols-2 gap-6">
        <GrowthChart />
        <BreakdownCharts />
      </div>
      <ExpiringSoon />
    </div>
  );
}

function ComplianceChart() {
  const [weeks, setWeeks] = useState<ComplianceWeek[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getCompliance().then((r) => setWeeks(r.weeks)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="bg-white border border-gray-200 rounded-xl p-6 h-72 animate-pulse" />;

  const data = weeks.map((w) => ({
    name: w.week_label?.replace("Week of ", "").split(" to ")[0] || w.week_start,
    compliance: w.compliance_pct,
    submitted: w.submitted,
    expected: w.expected,
  }));

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5 text-[#5f1b2c]" />
        <h2 className="text-lg font-semibold text-gray-900">Check-In Compliance Over Time</h2>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-35} textAnchor="end" height={60} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
            <Tooltip formatter={(value) => [`${value}%`, "Compliance"]} labelStyle={{ color: "#333" }} />
            <Bar dataKey="compliance" fill="#5f1b2c" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function RepeatMisses() {
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getRepeatMisses().then((r) => setMembers(r.members)).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="w-5 h-5 text-orange-500" />
        <h2 className="text-lg font-semibold text-gray-900">Repeat Missed Check-Ins (2+ in last 4 weeks)</h2>
      </div>
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-10 bg-gray-50 rounded-lg animate-pulse" />)}</div>
      ) : members.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">No members with repeated missed check-ins. Nice work!</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                <th className="px-3 py-2 font-medium">Member</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium text-center">Missed (4wk)</th>
                <th className="px-3 py-2 font-medium text-center">Missed (Month)</th>
                <th className="px-3 py-2 font-medium text-center">Total Missed</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m: any) => (
                <tr key={m.id} className="border-b border-gray-50">
                  <td className="px-3 py-2.5 font-medium text-gray-900">{m.name}</td>
                  <td className="px-3 py-2.5 text-gray-500 text-xs">{m.membership_type || "\u2014"}</td>
                  <td className="px-3 py-2.5 text-center"><span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">{m.missed_count}</span></td>
                  <td className="px-3 py-2.5 text-center"><span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">{m.missed_month ?? 0}</span></td>
                  <td className="px-3 py-2.5 text-center"><span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-700">{m.missed_total ?? 0}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PaymentIssuesList() {
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getPaymentIssues().then((r) => setMembers(r.members)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="bg-white border border-gray-200 rounded-xl p-6 h-32 animate-pulse" />;
  if (members.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="w-5 h-5 text-red-500" />
        <h2 className="text-lg font-semibold text-gray-900">Payment Issues ({members.length})</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-100">
              <th className="px-3 py-2 font-medium">Member</th>
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium">Plan</th>
              <th className="px-3 py-2 font-medium">Coach</th>
              <th className="px-3 py-2 font-medium text-right">Follow-Up Date</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m: any) => (
              <tr key={m.id} className="border-b border-gray-50">
                <td className="px-3 py-2.5 font-medium text-gray-900">
                  <Link to={`/members/${m.id}`} className="hover:text-[#5f1b2c] hover:underline">{m.name}</Link>
                </td>
                <td className="px-3 py-2.5 text-gray-500 text-xs">{m.membership_type || "\u2014"}</td>
                <td className="px-3 py-2.5 text-gray-500 text-xs">{m.payment_plan || "\u2014"}</td>
                <td className="px-3 py-2.5 text-gray-500 text-xs">{m.coach_assigned || "\u2014"}</td>
                <td className="px-3 py-2.5 text-right">
                  {m.follow_up_date ? (
                    <span className="text-xs text-red-600 font-medium">{fmtDate(m.follow_up_date)}</span>
                  ) : (
                    <span className="text-xs text-gray-400">Not set</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GrowthChart() {
  const [data, setData] = useState<{ growth: any[]; churn: any[] }>({ growth: [], churn: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getGrowth().then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="bg-white border border-gray-200 rounded-xl p-6 h-72 animate-pulse" />;

  // Merge growth and churn by month
  const months = new Set<string>();
  data.growth.forEach((g: any) => months.add(g.month));
  data.churn.forEach((c: any) => months.add(c.month));
  const sortedMonths = Array.from(months).sort();

  const chartData = sortedMonths.map((m) => ({
    month: m,
    new: data.growth.find((g: any) => g.month === m)?.new_members || 0,
    churned: data.churn.find((c: any) => c.month === m)?.churned || 0,
  }));

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-5 h-5 text-[#5f1b2c]" />
        <h2 className="text-lg font-semibold text-gray-900">Member Growth & Churn</h2>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="new" fill="#22c55e" name="New Members" radius={[4, 4, 0, 0]} />
            <Bar dataKey="churned" fill="#ef4444" name="Expired / Former" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function BreakdownCharts() {
  const [data, setData] = useState<{ by_plan: any[]; by_type: any[] }>({ by_plan: [], by_type: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getBreakdown().then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="bg-white border border-gray-200 rounded-xl p-6 h-72 animate-pulse" />;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Membership Breakdown</h2>
      <div className="grid grid-cols-2 gap-4">
        {/* By Type */}
        <div>
          <h3 className="text-xs font-medium text-gray-500 mb-2 text-center">By Type</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_type.map((t: any) => ({ name: t.membership_type || "Unknown", value: parseInt(t.count) }))} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#5f1b2c" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* By Plan */}
        <div>
          <h3 className="text-xs font-medium text-gray-500 mb-2 text-center">By Payment Plan</h3>
          <div className="space-y-2">
            {data.by_plan.map((p: any, i: number) => (
              <div key={p.payment_plan} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                <span className="text-xs text-gray-700 flex-1 truncate">{p.payment_plan || "Unknown"}</span>
                <span className="text-xs font-bold text-gray-900">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ExpiringSoon() {
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getExpiringSoon().then((r) => setMembers(r.members)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" id="expiring">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-5 h-5 text-orange-500" />
        <h2 className="text-lg font-semibold text-gray-900">Expiring Soon (Next 30 Days)</h2>
      </div>
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-10 bg-gray-50 rounded-lg animate-pulse" />)}</div>
      ) : members.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">No memberships expiring in the next 30 days.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                <th className="px-3 py-2 font-medium">Member</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Plan</th>
                <th className="px-3 py-2 font-medium">Coach</th>
                <th className="px-3 py-2 font-medium text-right">End Date</th>
                <th className="px-3 py-2 font-medium text-right">Days Left</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m: any) => {
                const endStr = toDateStr(m.end_date);
                const endDate = endStr ? new Date(endStr + "T00:00:00Z") : null;
                const daysLeft = endDate ? Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) : null;
                return (
                  <tr key={m.id} className="border-b border-gray-50">
                    <td className="px-3 py-2.5 font-medium text-gray-900">{m.name}</td>
                    <td className="px-3 py-2.5 text-gray-500 text-xs">{m.membership_type || "\u2014"}</td>
                    <td className="px-3 py-2.5 text-gray-500 text-xs">{m.payment_plan || "\u2014"}</td>
                    <td className="px-3 py-2.5 text-gray-500 text-xs">{m.coach_assigned || "\u2014"}</td>
                    <td className="px-3 py-2.5 text-gray-500 text-xs text-right">{fmtDate(m.end_date)}</td>
                    <td className="px-3 py-2.5 text-right">
                      {daysLeft !== null ? (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${daysLeft <= 7 ? "bg-red-100 text-red-700" : daysLeft <= 14 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{daysLeft}d</span>
                      ) : (
                        <span className="text-xs text-gray-400">\u2014</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}